import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Users, 
  Plus, 
  Trash2, 
  Loader2,
  User,
  CreditCard,
  MapPin,
  Phone,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PersonalData() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // New record form
  const [newRecord, setNewRecord] = useState({
    full_name: "",
    passport: "",
    city: "",
    phone: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/personal-data`, {
        headers: getAuthHeaders()
      });
      setRecords(response.data);
    } catch (error) {
      console.error("Error fetching records:", error);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newRecord.full_name.trim()) {
      toast.error("Введите ФИО");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/personal-data`, newRecord, {
        headers: getAuthHeaders()
      });
      toast.success("Запись добавлена");
      setNewRecord({ full_name: "", passport: "", city: "", phone: "" });
      fetchRecords();
    } catch (error) {
      toast.error("Ошибка добавления записи");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить запись?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/personal-data/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Запись удалена");
      fetchRecords();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const filteredRecords = records.filter(r => 
    r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-600" />
            Персональные данные
          </h1>
          <p className="text-slate-500 mt-1">
            Данные покупателей для генерации договоров купли-продажи
          </p>
        </div>
        <div className="text-sm text-slate-500">
          Всего записей: <span className="font-semibold text-slate-700">{records.length}</span>
        </div>
      </div>

      {/* Add New Record Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-green-600" />
            Добавить покупателя
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <User className="w-4 h-4" /> ФИО
              </Label>
              <Input
                placeholder="Иванов Иван Иванович"
                value={newRecord.full_name}
                onChange={(e) => setNewRecord({ ...newRecord, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <CreditCard className="w-4 h-4" /> Паспорт
              </Label>
              <Input
                placeholder="4515 123456"
                value={newRecord.passport}
                onChange={(e) => setNewRecord({ ...newRecord, passport: e.target.value })}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <MapPin className="w-4 h-4" /> Город
              </Label>
              <Input
                placeholder="Москва"
                value={newRecord.city}
                onChange={(e) => setNewRecord({ ...newRecord, city: e.target.value })}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Phone className="w-4 h-4" /> Телефон
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="+7 (999) 123-45-67"
                  value={newRecord.phone}
                  onChange={(e) => setNewRecord({ ...newRecord, phone: e.target.value })}
                />
                <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Поиск по ФИО или городу..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Records List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredRecords.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {searchTerm ? "Ничего не найдено" : "Нет записей. Добавьте первого покупателя."}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecords.map((record) => (
            <Card key={record.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{record.full_name}</h3>
                        {record.city && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {record.city}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      {record.passport && (
                        <p className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-slate-400" />
                          {record.passport}
                        </p>
                      )}
                      {record.phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {record.phone}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(record.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-blue-800 mb-2">Как это работает?</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• При генерации договора купли-продажи покупатель выбирается случайным образом</li>
            <li>• Цена продажи генерируется случайно: от 300 000 до 860 000 ₽</li>
            <li>• Дата продажи: случайная дата с 01 по 30 декабря 2026 года</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
